/* eslint-disable no-console */
import axios, { AxiosError } from 'axios';
import ENVIRONMENTS, {
  getKeycloakLoginUrl,
  getKeycloakRedirectUri,
  isConfiguredKeycloakUrl,
} from '../../environments';
import {
  setShowLoader,
  logout,
  dropRejectedSession,
} from '../../store/common/common.reducer';
import { persistor, store } from '../../store/create-store';
import { isPublicVisitorPath } from '../../app/premium-profile/profile-path';
import { isDevAuthBypass } from '../dev/dev-auth-bypass';
import { MOCK_AUTH_TOKEN } from '../dev/mock-member-user';
import { getOpenReplaySessionId } from '../services/analytics/analytics';
import {
  POST_AUTH_RETURN_KEY,
  getFreshToken,
  hasOpenDialog,
  inPageRenewalWorks,
  isOnAuthCallback,
  markTopLevelRenewal,
  renewSessionInPage,
  renewSessionUnlessCoolingDown,
  tokenHasExpired,
  tokenIsExpiring,
  tokenNeedsRenewal,
  topLevelRenewalIsRecent,
  whenNoDialogIsOpen,
} from './session-renewal';

const MAX_DIALOG_HOLD_MS = 3 * 60 * 1000;
const LEAVING_TIMEOUT_MS = 30 * 1000;
const REJECTED_FRESH_TOKEN_GAP_MS = 10 * 60 * 1000;
let sessionRenewInFlight = false;
let requestsInFlight = 0;

export const hasRequestsInFlight = (): boolean => requestsInFlight > 0;

export const reloadIfRestoredMidTrip = (event: PageTransitionEvent): void => {
  if (event.persisted && sessionRenewInFlight) window.location.reload();
};

window.addEventListener('pageshow', reloadIfRestoredMidTrip);

/** Re-auth via Keycloak SSO instead of ending the session on an expired access token. */
export const renewSessionAtTopLevel = (
  from = 'SilentRenew',
  minGapMs?: number,
): boolean => {
  if (sessionRenewInFlight || isOnAuthCallback()) return false;
  if (topLevelRenewalIsRecent(Date.now(), minGapMs)) {
    console.warn('Skipped a Keycloak round trip: the last one was moments ago');
    return false;
  }
  const { pathname, search, hash } = window.location;
  try {
    sessionStorage.setItem(POST_AUTH_RETURN_KEY, `${pathname}${search}${hash}`);
  } catch {
    /* ignore */
  }
  if (!isConfiguredKeycloakUrl(ENVIRONMENTS.KEYCLOAK_ACCOUNT)) {
    store.dispatch(logout());
    return false;
  }
  sessionRenewInFlight = true;
  const state = markTopLevelRenewal(getKeycloakRedirectUri());
  persistor.pause();
  persistor.flush();
  localStorage.removeItem('persist:root');
  window.location.assign(
    `${getKeycloakLoginUrl(from)}&state=${encodeURIComponent(state)}`,
  );
  return true;
};

const isSessionExchange = (url?: string) =>
  String(url || '').includes('/auth/login') ||
  String(url || '').includes('/auth/logout');

const saysJwtExpired = (data: any) =>
  /jwt expired/i.test(String(data?.message || ''));

const staysOnPage = (pathname: string, expired: boolean) =>
  (pathname.startsWith('/onboarding') && !expired) ||
  isPublicVisitorPath(pathname);

const endRejectedSession = (expired: boolean) => {
  const { pathname } = window.location;
  if (pathname.startsWith('/onboarding') && !expired) return;
  if (isPublicVisitorPath(pathname)) {
    store.dispatch(dropRejectedSession());
    return;
  }
  renewSessionAtTopLevel(
    'SilentRenew',
    expired ? undefined : REJECTED_FRESH_TOKEN_GAP_MS,
  );
};

const recoverExpiredSession = (expired: boolean) => {
  if (staysOnPage(window.location.pathname, expired) || !hasOpenDialog()) {
    endRejectedSession(expired);
    return;
  }
  whenNoDialogIsOpen(() => {
    const token = store.getState().common.auth?.token;
    if (!token || !tokenNeedsRenewal(token)) return;
    renewSessionInPage().catch(() => {
      const current = store.getState().common.auth?.token;
      if (current) endRejectedSession(expired || tokenHasExpired(current));
    });
  }, MAX_DIALOG_HOLD_MS);
};

const leavesForKeycloakFirst = (token?: string) =>
  Boolean(token) &&
  tokenIsExpiring(token) &&
  !inPageRenewalWorks() &&
  !hasOpenDialog() &&
  !isPublicVisitorPath(window.location.pathname) &&
  (sessionRenewInFlight || renewSessionAtTopLevel());

const apiV2Instance = axios.create({
  baseURL: ENVIRONMENTS.API_BASE_URL_V2,
  timeout: 15000,
});

let activeLoaderRequests = 0;

const syncGlobalLoader = (visible: boolean) => {
  if (visible) {
    activeLoaderRequests += 1;
    store.dispatch(setShowLoader(true));
    return;
  }
  activeLoaderRequests = Math.max(0, activeLoaderRequests - 1);
  if (activeLoaderRequests === 0) {
    store.dispatch(setShowLoader(false));
  }
};

apiV2Instance.interceptors.request.use(
  async config => {
    const params = new URLSearchParams(window.location.search);
    requestsInFlight += 1;
    if (!config.silent) syncGlobalLoader(true);
    const exchanging = isSessionExchange(config.url) || isOnAuthCallback();
    const token = exchanging
      ? store.getState().common.auth?.token
      : await getFreshToken();
    if (!exchanging && leavesForKeycloakFirst(token)) {
      return new Promise<never>((_, reject) => {
        window.setTimeout(
          () =>
            reject(
              Object.assign(new Error('Signing in again first'), { config }),
            ),
          LEAVING_TIMEOUT_MS,
        );
      });
    }
    if (token || params.get('token')) {
      // eslint-disable-next-line no-param-reassign
      config.headers = {
        ...config.headers,
        Authorization: `Bearer ${token || params.get('token')}`,
      };
    } else {
      console.log('no token', config.url);
    }
    const openreplaySessionId = getOpenReplaySessionId();
    if (openreplaySessionId) {
      // eslint-disable-next-line no-param-reassign
      config.headers = {
        ...config.headers,
        'x-openreplay-session-id': openreplaySessionId,
      };
    }
    return config;
  },
  (error: AxiosError) => {
    console.error(error);
    syncGlobalLoader(false);
    return Promise.reject(error.toJSON());
  },
);

apiV2Instance.interceptors.response.use(
  response => {
    requestsInFlight = Math.max(0, requestsInFlight - 1);
    if (!response.config.silent) syncGlobalLoader(false);
    return response;
  },
  async (error: AxiosError) => {
    console.error(error);
    requestsInFlight = Math.max(0, requestsInFlight - 1);
    if (!error.config?.silent) syncGlobalLoader(false);
    if (error.response?.status === 401) {
      const { common } = store.getState();
      const token = common.auth?.token;
      const reqUrl = String(error.config?.url || '');
      // Never logout on failed credential exchange — that dumps users on `/`.
      const isAuthExchange = isSessionExchange(reqUrl);
      const willRenew = Boolean(
        token &&
        !isAuthExchange &&
        !isOnAuthCallback() &&
        !(isDevAuthBypass && token === MOCK_AUTH_TOKEN),
      );
      if (willRenew && error.config && !error.config.sessionRetried) {
        const sentStaleToken =
          error.config.headers?.Authorization !== `Bearer ${token}`;
        const canRetry =
          sentStaleToken ||
          (await renewSessionUnlessCoolingDown().then(
            () => true,
            () => false,
          ));
        if (canRetry) {
          return apiV2Instance({ ...error.config, sessionRetried: true });
        }
      }
      // Do not wipe the local mock session when APIs reject the fake token
      const current = store.getState().common.auth?.token;
      if (willRenew && current) {
        recoverExpiredSession(
          tokenHasExpired(current) || saysJwtExpired(error.response.data),
        );
      }
    }
    if (error.response) {
      return Promise.reject(error.response);
    }
    const networkError = new Error(
      error.message || error.code || 'Network Error',
    );
    return Promise.reject(networkError);
  },
);

export { apiV2Instance };
